import React, { useEffect, useMemo, useState } from "react";
import { Loader } from "@mantine/core";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import { getDepartmentPairs } from "./collaborationData";
import useDepartmentColors from "./useDepartmentColors";
import css from "./DepartmentConnectionCard.module.scss";

const hoursLabel = (hours) =>
  Number.isFinite(hours)
    ? `${hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`
    : "—";

export default function DepartmentConnectionCard({
  data,
  isLoading,
  isError,
  uid,
  duration,
}) {
  const { openDrilldown } = useInsightsDrilldown();
  const [hoveredDept, setHoveredDept] = useState(null);
  const [hoveredPair, setHoveredPair] = useState(null);
  const pairs = useMemo(() => getDepartmentPairs(data), [data]);
  useEffect(() => {
    setHoveredDept(null);
    setHoveredPair(null);
  }, [data, duration, uid]);
  const departments = useMemo(
    () => [...new Set(data?.departments || [])],
    [data?.departments],
  );
  const departmentColors = useDepartmentColors(departments, uid);
  const dense = departments.length > 5;
  const width = Math.max(640, departments.length * 105);
  const height = dense ? 420 : 300;
  const stats = departments
    .map((name) => {
      const connections = pairs.filter((pair) => pair.names.includes(name));
      return {
        name,
        connections: connections.length,
        volume: connections.reduce((sum, pair) => sum + pair.meetings, 0),
      };
    })
    .sort((a, b) => b.volume - a.volume || a.name.localeCompare(b.name));
  const maxVolume = Math.max(1, ...stats.map((item) => item.volume));
  const maxMeetings = Math.max(1, ...pairs.map((pair) => pair.meetings));
  const orderedStats = dense
    ? [...stats].sort((a, b) => a.name.localeCompare(b.name))
    : stats;
  const nodes = orderedStats.map((stat, index) => {
    const angle = -Math.PI / 2 - (index / stats.length) * Math.PI * 2;
    const x =
      stats.length === 2
        ? width * (index ? 0.73 : 0.27)
        : width / 2 + Math.cos(angle) * (width / 2 - 95);
    const y =
      stats.length === 2
        ? height / 2
        : height / 2 + Math.sin(angle) * (height / 2 - 65);
    return {
      ...stat,
      x,
      y,
      radius: 30 + 16 * Math.sqrt(stat.volume / maxVolume),
      ...departmentColors.get(stat.name),
    };
  });
  const activePair = pairs.find((pair) => pair.id === hoveredPair);
  const activeDept = nodes.find((node) => node.name === hoveredDept);
  const canDrill = Boolean(uid && duration);
  const openPair = (pair) => {
    if (!canDrill) return;
    openDrilldown({
      source: pair.source,
      target: pair.target,
      name: `${pair.source} - ${pair.target} meetings`,
      type: "Department Collaboration",
      redisKey: `users:${uid}:orgCalculations:departmentCollaboration:${duration}:all:${pair.source}|${pair.target}`,
    });
  };
  return (
    <section className={css.card} aria-label="Departments collaboration">
      <header className={css.header}>
        <div>
          <h3>Departments collaboration</h3>
          <p>
            Line thickness shows shared meetings. Circle size shows
            collaboration volume.
          </p>
        </div>
        {!isLoading && !isError && pairs.length > 0 && (
          <span>
            {departments.length} departments · {pairs.length}{" "}
            {pairs.length === 1 ? "connection" : "connections"}
          </span>
        )}
      </header>
      {isLoading ? (
        <div className={css.state} role="status">
          <Loader size="sm" />
          Loading department connections...
        </div>
      ) : isError ? (
        <div className={css.state} role="alert">
          Failed to load department connections
        </div>
      ) : !pairs.length ? (
        <div className={css.state}>
          No department interaction data available
        </div>
      ) : (
        <div className={css.layout}>
          <div>
            <div className={`${css.network} ${dense ? css.denseNetwork : ""}`}>
              <svg
                viewBox={`0 0 ${width} ${height}`}
                style={{ minWidth: dense ? width : undefined }}
                aria-label="Department collaboration network"
              >
                {pairs.map((pair) => {
                  const source = nodes.find(
                    (node) => node.name === pair.source,
                  );
                  const target = nodes.find(
                    (node) => node.name === pair.target,
                  );
                  const active =
                    hoveredPair === pair.id || pair.names.includes(hoveredDept);
                  const muted = (hoveredPair || hoveredDept) && !active;
                  const midX = (source.x + target.x) / 2;
                  const midY = (source.y + target.y) / 2;
                  const stroke = 2 + (pair.meetings / maxMeetings) * 8;
                  return (
                    <g
                      key={pair.id}
                      className={css.connection}
                      style={{ opacity: muted ? 0.2 : 1 }}
                      role="button"
                      tabIndex={canDrill ? 0 : -1}
                      aria-disabled={!canDrill}
                      aria-label={`View ${pair.names.join(" and ")} meetings`}
                      onClick={() => openPair(pair)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openPair(pair);
                        }
                      }}
                      onMouseEnter={() => setHoveredPair(pair.id)}
                      onMouseLeave={() => setHoveredPair(null)}
                      onFocus={() => setHoveredPair(pair.id)}
                      onBlur={() => setHoveredPair(null)}
                    >
                      <line
                        x1={source.x}
                        y1={source.y}
                        x2={target.x}
                        y2={target.y}
                        stroke={active ? "#39a8f0" : "#b8dbf3"}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                      />
                      <line
                        x1={source.x}
                        y1={source.y}
                        x2={target.x}
                        y2={target.y}
                        stroke="transparent"
                        strokeWidth="22"
                      />
                      {(!dense || hoveredPair === pair.id) && (
                        <g>
                          <rect
                            x={midX - 20}
                            y={midY - 11}
                            width="40"
                            height="22"
                            rx="11"
                            fill="white"
                            stroke={active ? "#39a8f0" : "#c6e3f8"}
                          />
                          <text
                            x={midX}
                            y={midY + 4}
                            textAnchor="middle"
                            fill="#175f8f"
                            fontSize="12"
                            fontWeight="600"
                          >
                            {pair.meetings}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
                {nodes.map((node) => (
                  <g
                    key={node.name}
                    tabIndex={0}
                    className={css.node}
                    role="img"
                    aria-label={`${node.name}: ${node.connections} connections, ${node.volume} shared meetings across department pairs`}
                    onMouseEnter={() => setHoveredDept(node.name)}
                    onMouseLeave={() => setHoveredDept(null)}
                    onFocus={() => setHoveredDept(node.name)}
                    onBlur={() => setHoveredDept(null)}
                    style={{
                      opacity:
                        activePair && !activePair.names.includes(node.name)
                          ? 0.35
                          : 1,
                    }}
                  >
                    <title>
                      {node.name}: {node.volume} shared meetings across
                      department pairs
                    </title>
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={node.radius}
                      fill={node.color}
                      stroke={hoveredDept === node.name ? "#39a8f0" : "#fff"}
                      strokeWidth="2"
                    />
                    <text
                      x={node.x}
                      y={node.y - 2}
                      textAnchor="middle"
                      fill={node.ink}
                      fontSize="12"
                      fontWeight="600"
                    >
                      {node.name.length > 13
                        ? `${node.name.slice(0, 12)}…`
                        : node.name}
                    </text>
                    <text
                      x={node.x}
                      y={node.y + 16}
                      textAnchor="middle"
                      fill={node.ink}
                      fontSize="10"
                    >
                      {node.connections}{" "}
                      {node.connections === 1 ? "connection" : "connections"}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
            <p className={css.caption} aria-live="polite">
              {activeDept
                ? `${activeDept.name}: ${activeDept.volume} shared meetings across ${activeDept.connections} department pairs.`
                : "Meetings involving several departments count toward each pair."}
            </p>
          </div>
          <div className={css.listColumn}>
            <div className={css.listHeader}>
              <span>Departments</span>
              <span>Meetings</span>
              <span>Hours</span>
            </div>
            <div className={css.list}>
              {pairs.map((pair) => (
                <button
                  type="button"
                  key={pair.id}
                  disabled={!canDrill}
                  className={`${css.row} ${hoveredPair === pair.id || pair.names.includes(hoveredDept) ? css.active : ""}`}
                  onMouseEnter={() => setHoveredPair(pair.id)}
                  onMouseLeave={() => setHoveredPair(null)}
                  onFocus={() => setHoveredPair(pair.id)}
                  onBlur={() => setHoveredPair(null)}
                  onClick={() => openPair(pair)}
                >
                  <span>{pair.names.join(" ↔ ")}</span>
                  <strong>{pair.meetings.toLocaleString()}</strong>
                  <span>{hoursLabel(pair.hours)}</span>
                </button>
              ))}
            </div>
            <p className={css.hint}>Select a connection to see its meetings.</p>
          </div>
        </div>
      )}
    </section>
  );
}
